import { gql, useQuery, useMutation } from '@apollo/client'

const GET_ORDER_BOQS_QUERY = gql`
  query GetOrderBoqs($orderId: String!) {
    order(number: $orderId) {
      id
      boqs {
        nodes {
          id
          boqFiles {
            totalCount
          }
        }
      }
    }
  }
`

export const useGetOrderBoqs = (orderId) => {
  const { loading, error, data } = useQuery(GET_ORDER_BOQS_QUERY, {
    variables: { orderId }
  })

  // NOTE: Cannot use data?.order?.boqs?.nodes || [] because babel error
  const { order } = data || {}
  const { boqs } = order || {}
  const { nodes: boqsNodes } = boqs || {}
  const boqsList = boqsNodes ? boqsNodes.map((boq) => {
    const { boqFiles } = boq || {}
    const { totalCount } = boqFiles || {}

    return { id: boq.id, totalCount }
  }) : []

  return {
    loading,
    error,
    boqs: boqsList
  }
}

const GET_BOQ_FILES_QUERY = gql`
  query GetBoqItems($boqId: ID!) {
    boq(boqId: $boqId) {
      id
      boqContext
      boqFiles {
        nodes {
          id
          processStatus
          errorMessage
          filename
          value
          contentType
          boqItems {
            nodes {
              id
              name
              searchQuery
              qty
              unit
              remark
              versionCode
              boqItemClosestInference {
                id
                qty
                unitConversionRemark
                versionCode
                item {
                  id
                  name
                  nameTh
                  nameEn
                  unit
                  url
                  cost
                  weight
                  price
                }
              }
              boqItemAlternativeInferences {
                nodes {
                  id
                  qty
                  unitConversionRemark
                  versionCode
                  item {
                    id
                    name
                    nameTh
                    nameEn
                    unit
                    url
                    cost
                    weight
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export const useGetBoqFiles = (boqId) => {
  const { loading, error, data } = useQuery(GET_BOQ_FILES_QUERY, {
    variables: { boqId },
    skip: !boqId,
  })

  // NOTE: Cannot use data?.boq?.boqFiles?.nodes || [] because babel error
  const { boq } = data || {}
  const { boqFiles, boqContext } = boq || {}
  const { nodes: boqFilesNodes } = boqFiles || {}
  const parseBoqFiles = boqFilesNodes ? boqFilesNodes.map((boqFile) => {
    const { boqItems } = boqFile || {}
    const { nodes: boqItemsNodes } = boqItems || {}

    return {
      id: boqFile.id,
      processStatus: boqFile.processStatus,
      errorMessage: boqFile.errorMessage,
      value: boqFile.value,
      filename: boqFile.filename,
      contentType: boqFile.contentType,
      boqItems: boqItemsNodes ? boqItemsNodes.map((item) => ({
        id: item.id,
        name: item.name,
        searchQuery: item.searchQuery,
        qty: item.qty,
        unit: item.unit,
        remark: item.remark,
        versionCode: item.versionCode,
        boqItemClosestInference: item.boqItemClosestInference,
        boqItemAlternativeInferences: item.boqItemAlternativeInferences.nodes || []
      })) : []
    }
  }) : []

  return {
    loading,
    error,
    boqContext,
    boqFiles: parseBoqFiles
  }
}

const CREATE_BOQ_MUTATION = gql`
  mutation CreateBoq(
    $orderNumber: String!,
    $boqFileUrls: [String!],
    $boqContext: String,
    $boqText: String
  ) {
    createBoq(
      orderNumber: $orderNumber,
      boqFileUrls: $boqFileUrls,
      boqContext: $boqContext,
      boqText: $boqText
    ) {
      errors {
        key
        message
      }
      boq {
        id
      }
      order {
        id
        boqs {
          nodes {
            id
            boqFiles {
              totalCount
            }
          }
        }
      }
    }
  }
`

export const useCreateBoq = () => {
  return useMutation(CREATE_BOQ_MUTATION)
}

const PROCESS_BOQ_MUTATION = gql`
  mutation ProcessBoq($boqId: String!) {
    processBoq(boqId: $boqId) {
      errors {
        key
        message
      }
    }
  }
`

export const useProcessBoq = () => {
  return useMutation(PROCESS_BOQ_MUTATION)
}

const ADD_CLOSEST_ITEMS_TO_ORDER_MUTATION = gql`
  mutation AddClosestItems($boqId: String!, $orderNumber: String!) {
    addLineItemsFromClosestBoqInference(boqId: $boqId, orderNumber: $orderNumber) {
      errors {
        key
        message
      }
    }
  }
`

export const useAddClosestItemsToOrder = () => {
  return useMutation(ADD_CLOSEST_ITEMS_TO_ORDER_MUTATION)
}